import { auth, db } from "@/firebase";
import { ref, update, get } from "firebase/database";
import { getIdTokenResult } from "firebase/auth";
import { defineStore } from "pinia";

export const useConfigStore = defineStore("config", {
    state: () => {
        return {
            HARDCODED_PATH: null,
            formEmail: null,
            currency: "kr.",
            hotelFullName: null,
            hotelLocationInitals: null,
            isSingleLanguage: false,
            isStatisticsDisabled: false,
            isRoomsDisabled: false,
            isMessagesDisabled: false,
            isMultiUser: false,
            isAdminUser: false,
            isLoaded: false,
            loadingPromise: null
        };
    },
    actions: {
        // Ensures that the configuration is loaded before accessing it. 
        // If it's already loaded, it returns immediately.
        // Used in route guards.
        async ensureLoading(hotelId) {
            if (this.isLoaded) return;
            if (this.loadingPromise) return this.loadingPromise;

            this.loadingPromise = this.loadConfiguration(hotelId).then(() => {
                this.isLoaded = true;
            })
            try {
                await this.loadingPromise;
            } finally {
                this.loadingPromise = null;
            }
        },
        async loadConfiguration(hotelId) {
            await Promise.all([
                this.getHotelEmail(hotelId).then((email) => this.formEmail = email),
                this.getHotelCurrency(hotelId).then((currency) => this.currency = currency),
                this.getHotelFullName(hotelId).then((fullName) => this.hotelFullName = fullName),
                this.getHotelLocationInitals(hotelId).then((locationInitals) => this.hotelLocationInitals = locationInitals),
                this.getIsSingleLanguage(hotelId).then((isSingleLanguage) => this.isSingleLanguage = isSingleLanguage),
                this.getIsStatisticsDisabled(hotelId).then((isStatisticsDisabled) => this.isStatisticsDisabled = isStatisticsDisabled),
                this.getIsRoomsDisabled(hotelId).then((isRoomsDisabled) => this.isRoomsDisabled = isRoomsDisabled),
                this.getIsMessagesDisabled(hotelId).then((isMessagesDisabled) => this.isMessagesDisabled = isMessagesDisabled),
                this.getIsMultiUser().then((isMultiUser) => this.isMultiUser = isMultiUser),
                this.getIsAdminUser().then((isAdminUser) => this.isAdminUser = isAdminUser),
            ]);
            this.isLoaded = true;
        },
        async getHotelEmail(hotelID) {
            const snapshot = await get(ref(db, `/settings/${hotelID}/smsEmail/emailAddress`));
            return snapshot.val();
        },
        async getHotelCurrency(hotelID) {
            const snapshot = await get(ref(db, `/settings/${hotelID}/smsEmail/currency`));
            return snapshot.val() || "kr.";
        },
        async getHotelFullName(hotelID) {
            const snapshot = await get(ref(db, `/settings/${hotelID}/smsEmail/hotelFullName`));
            return snapshot.val();
        },
        async getHotelLocationInitals(hotelID) {
            const snapshot = await get(ref(db, `/hotelLocation/${hotelID}`));
            return snapshot.val();
        },
        async getIsSingleLanguage(hotelID) {
            const snapshot = await get(ref(db, `/hotels/${hotelID}/hotelLanguages`));
            const { languageTypes, languages } = snapshot.val();

            if (languages?.length) {
                return languageTypes.length === 1 && languages.length === 1;
            }

            if (
                !languageTypes &&
                !languageTypes.length &&
                languages &&
                languages.length
            ) {
                return languages.length === 1;
            }
            return true;
        },
        async getIsStatisticsDisabled(hotelID) {
            const snapshot = await get(ref(db, `/settings/${hotelID}/smsEmail/isStatisticsDisabled`));
            return snapshot.val();
        },
        async getIsRoomsDisabled(hotelID) {
            const snapshot = await get(ref(db, `/settings/${hotelID}/smsEmail/isRoomsDisabled`));
            return snapshot.val();
        },
        async getIsMessagesDisabled(hotelID) {
            const snapshot = await get(ref(db, `/settings/${hotelID}/smsEmail/isMessagesDisabled`));
            return snapshot.val();
        },
        async getIsMultiUser() {
            const token = await getIdTokenResult(auth.currentUser);
            return token.claims.multiuser ?? false;
        },
        async getIsAdminUser() {
            const token = await getIdTokenResult(auth.currentUser);
            return token.claims.internal ?? false;
        },
        async getCorrectPath(hotelId) {
            this.HARDCODED_PATH = import.meta.env.VITE_PATH;
            const isCustomDomain = await get(
                ref(db, `/scandicdomainhotels/${hotelId}`)
            );
            if (isCustomDomain.exists()) {
                this.HARDCODED_PATH = import.meta.env.VITE_PATH_SCANDIC;
            }
        },
        async getUserNode(userId) {
            const snapshot = await get(ref(db, `users/${userId}/`));
            return snapshot.val();
        },
        async updateLastLogin(userId, date) {
            return update(ref(db, `/users/${userId}`), {
                lastLoginDate: date.toJSON().slice(0, 10).replace(/-/g, "/"),
            });
        },
    },
    getters: {
        correctPath: (state) => state.HARDCODED_PATH,
    }
});